import {
    AccountCircle as AccountIcon,
    Close as CloseIcon,
    Lock as LockIcon,
    Star as StarIcon
} from "@mui/icons-material";
import Logger from "../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AuthService from "../../services/AuthService";
import SubscriptionService from "../../services/SubscriptionService";
import { usePagesTranslation } from "../locales";
import StripeClimateBadgeIframe from "../components/StripeClimateBadgeIframe";
import {
    SUBSCRIPTION_PLANS,
    DEFAULT_SELECTED_PLAN,
    DEFAULT_TUTOR_PLAN,
    PremiumPlanFeaturesSection,
    PlanPickerFootnotes,
    getSubscriptionCatalogFromPath,
    getSubscriptionPlanPickerEntries,
    getSubscriptionPlanLabel,
    PlanCardOfferExtras,
} from "./PanelPlan";

const SubscriptionDialog = ({
    open,
    onClose,
    triggerReason = 'general',
    userSubscription = 'free',
    initialSelectedPlan = null
}) => {
    const { t } = usePagesTranslation();
    const location = useLocation();
    const catalog = getSubscriptionCatalogFromPath(location.pathname);
    const defaultPlan = catalog === "tutors" ? DEFAULT_TUTOR_PLAN : DEFAULT_SELECTED_PLAN;
    const [, setUser] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(initialSelectedPlan || defaultPlan);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    const planEntries = getSubscriptionPlanPickerEntries({ hideMultiMonthPlans: true, catalog });
    const planPickerKeys = planEntries.map(([key]) => key);

    useEffect(() => {
        if (open && initialSelectedPlan) {
            setSelectedPlan(
                SUBSCRIPTION_PLANS[initialSelectedPlan]
                    ? initialSelectedPlan
                    : defaultPlan,
            );
        } else if (!open) {
            setSelectedPlan(defaultPlan);
        }
    }, [open, initialSelectedPlan, defaultPlan]);

    useEffect(() => {
        const getUser = async () => {
            try {
                // Check if user is authenticated before calling getUser
                const isAuthenticated = await AuthService.isAuthenticated();
                setIsAuthenticated(isAuthenticated);
                if (isAuthenticated) {
                    const userInfo = await AuthService.getUser();
                    setUser(userInfo);
                }
            } catch (error) {
                // Silently handle unauthenticated users - this is expected for anonymous users
                if (error.name !== 'UserUnAuthenticatedException') {
                    Logger.error("Error getting user:", error);
                }
                setIsAuthenticated(false);
            }
        };

        getUser();
    }, []);

    useEffect(() => {
        const onPageShow = (event) => {
            if (event.persisted) {
                setIsProcessing(false);
            }
        };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
    }, []);

    const getTriggerMessage = () => {
        switch (triggerReason) {
            case 'save':
                return t("subs.dialog.trigger-save");
            case 'puzzle':
                return t("subs.dialog.trigger-puzzle");
            case 'hint':
                return t("subs.dialog.trigger-hint");
            default:
                return catalog === "tutors"
                    ? t("subs.dialog.trigger-tutors", "Choose a Tutor Standard plan for lessons, vocabulary, and printable worksheets.")
                    : t("subs.dialog.trigger-general");
        }
    };

    const handleSubscribe = async () => {
        setIsProcessing(true);
        setError('');
        try {
            const result = await SubscriptionService.handleSubscribe(selectedPlan, SUBSCRIPTION_PLANS);

            if (result.requiresLogin) {
                setShowLoginPrompt(true);
                return;
            }

            if (!result.success) {
                setError(result.error || t("profile.userProfile.snackbar-upgrade-error"));
                return;
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleLogin = () => {
        setShowLoginPrompt(false);
        // Redirect to login page or show login modal
        window.location.href = '/login';
    };


    const resetModal = () => {
        setError('');
        setIsProcessing(false);
        setShowLoginPrompt(false);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 2,
                    maxWidth: 980,
                    width: '100%',
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: '#ffd700', fontSize: 28 }} />
                    <Typography variant="h5" component="div">
                        {catalog === "tutors"
                            ? t("subs.dialog.tutors-title", "Tutor Standard")
                            : t("subs.dialog.title")}
                    </Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                {/* Login Prompt */}
                {showLoginPrompt && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <AccountIcon />
                            <Box>
                                <Typography variant="body2" fontWeight="bold">
                                    {t("subs.dialog.login-required")}
                                </Typography>
                                <Typography variant="body2">
                                    {t("subs.dialog.login-required-desc")}
                                </Typography>
                            </Box>
                        </Box>
                    </Alert>
                )}

                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Plan Selection */}
                <>
                    {/* Trigger Message */}
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {getTriggerMessage()}
                    </Typography>

                    {/* Plan Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t("subs.dialog.choose-plan")}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, pt: 2, flexWrap: 'wrap' }}>
                            {planEntries.map(([key, plan]) => (
                                <Card
                                    key={key}
                                    sx={{
                                        flex: {
                                            xs: '1 1 100%',
                                            sm: '1 1 calc(50% - 8px)',
                                            md: catalog === "tutors" ? '1 1 calc(50% - 8px)' : '1 1 calc(25% - 12px)'
                                        },
                                        minWidth: { sm: 140, md: 0 },
                                        cursor: 'pointer',
                                        border: selectedPlan === key ? '2px solid #3BA666' : '1px solid #e0e0e0',
                                        position: 'relative',
                                        overflow: 'visible',
                                        '&:hover': {
                                            borderColor: '#3BA666'
                                        }
                                    }}
                                    onClick={() => setSelectedPlan(key)}
                                >
                                    {plan.popular && (
                                        <Chip
                                            label={t("subs.dialog.most-popular")}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -12,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: '#3BA666',
                                                color: 'white',
                                                fontSize: '0.7rem',
                                                whiteSpace: 'nowrap',
                                                zIndex: 1
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ textAlign: 'center', pt: plan.popular ? 3 : 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {getSubscriptionPlanLabel(t, key)}
                                        </Typography>
                                        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                            {plan.price}
                                        </Typography>
                                        <PlanCardOfferExtras planKey={key} plan={plan} />
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                        <PlanPickerFootnotes planKeys={planPickerKeys} catalog={catalog} />
                    </Box>

                    {catalog !== "tutors" ? <PremiumPlanFeaturesSection /> : null}
                    <StripeClimateBadgeIframe sx={{ mt: 0, mb: 3 }} />

                    {/* Current Plan Info */}
                    {isAuthenticated && userSubscription === 'free' && (
                        <Box sx={{
                            p: 2,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 3
                        }}>
                            <LockIcon color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                                {t("subs.dialog.free-plan-msg")}
                            </Typography>
                        </Box>
                    )}
                </>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2, gap: 1 }}>
                {showLoginPrompt ? (
                    <>
                        <Button onClick={handleClose} variant="outlined">
                            {t("subs.dialog.cancel")}
                        </Button>
                        <Button
                            onClick={handleLogin}
                            variant="contained"
                            sx={{
                                backgroundColor: '#3BA666',
                                '&:hover': { backgroundColor: '#2e7d32' }
                            }}
                        >
                            {t("subs.dialog.login-signup")}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={handleClose} variant="outlined">
                            {t("subs.dialog.maybe-later")}
                        </Button>
                        <Button
                            onClick={handleSubscribe}
                            variant="contained"
                            disabled={isProcessing}
                            sx={{
                                backgroundColor: '#3BA666',
                                '&:hover': { backgroundColor: '#2e7d32' }
                            }}
                        >
                            {isProcessing ? t("subs.dialog.processing") : t("subs.dialog.upgrade-now")}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionDialog;

